import {
  CollapseButton,
  CollapseSection,
  EditableTypography,
  isImageOr3dModel,
  isPdfOrFile,
  processAttachment,
  TextWithOverflow,
} from "@msys/ui";
import { Box, Button, List, Stack, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { ContractType } from "../../../../clients/graphqlTypes.js";
import { NoPropagateBox } from "../../../commons/NoPropagateBox.js";
import { GalleryRow } from "../../../commons/images/GalleryRow.js";
import { TIME_AND_MATERIAL_CONTRACT_TYPES } from "../../../constants.js";
import {
  ALL_KEY,
  ExpandedStore,
} from "../../../trees-virtual/hooks/useExpandedStore.js";
import { FileListItemButton } from "../../../commons/files/FileListItemButton.js";
import type { ModifyItemCalculationFn } from "../../doc-data-grid/useModifyItemCalculation.js";
import type { ModifyItemInfoFn } from "../../doc-data-grid/useModifyItemInfo.js";
import type { ModifyItemProductFn } from "../../doc-data-grid/useModifyItemProduct.js";
import { ContingencyCheckbox } from "../../doc-items/fields/ContingencyCheckbox.js";
import { DecisionOptionRadioOrCheckbox } from "../../doc-items/fields/DecisionOptionRadioOrCheckbox.js";
import { SubitemsOptionsNoneSection } from "../../doc-items/fields/SubitemsOptionsNoneSection.js";
import { useDecisionItemMutations } from "../../doc-items/hooks/useDecisionItemMutations.js";
import { noopRefetchWizardQuestions } from "../../doc-items/withWizardRefetch.js";
import { DocumentPreviewItems } from "../../documents/DocumentPreviewItems.js";
import { Fields } from "../../documents/DocumentPreviewItemsTypes.js";
import { EditableQuantityCell } from "../../quotes/preview/EditableQuantityCell.js";
import {
  templateQuoteItemHasDescription,
  TemplateQuotePreviewItemDescription,
} from "./TemplateQuotePreviewItemDescription.js";
import { TemplateQuotePreviewItems_ItemFragment } from "./TemplateQuotePreviewItems.generated.js";

// Stable module-level noops for the mutation defaults. Re-using these (rather
// than allocating fresh `async () => {}` instances per render) keeps the
// reference identity stable so the `fields` useMemo below remains memoized
// for read-only consumers that omit the mutation props.
const noopModifyItemInfo: ModifyItemInfoFn = async () => {};
const noopModifyItemProduct: ModifyItemProductFn = async () => {};
const noopModifyItemCalculation: ModifyItemCalculationFn = async () => {};

export const TemplatesQuotePreviewItems = ({
  docId,
  canEdit,
  navigateToItem,
  selectedItemId,
  allDocItems,
  contractType,
  isBinding,
  container,
  expandedStore,
  refetch,
  modifyItemInfo = noopModifyItemInfo,
  modifyItemProduct = noopModifyItemProduct,
  modifyItemCalculation = noopModifyItemCalculation,
}: {
  docId: string;
  canEdit: boolean;
  navigateToItem?: (item: TemplateQuotePreviewItems_ItemFragment) => void;
  selectedItemId?: string | null;
  allDocItems: TemplateQuotePreviewItems_ItemFragment[];
  contractType: ContractType;
  isBinding: boolean;
  container?: HTMLElement | null;
  expandedStore: ExpandedStore;
  refetch?: () => Promise<unknown>;
  // Mutation functions are passed in by the editor route (which owns the
  // hooks) so this component does not statically import them. Required when
  // `canEdit` is `true`; read-only consumers (e.g. `TemplatesQuoteOverviewModal`,
  // `TemplateQuoteVersion`) omit them — the editable branches are gated on
  // `canEdit` so the props are never read there. Keeping these as props
  // (instead of `useModifyItem*` hooks inside this component) prevents
  // read-only consumers from transitively pulling `ProductBox.tsx` — and its
  // Vite-only SVG / `import.meta` imports — into their Jest test graph.
  modifyItemInfo?: ModifyItemInfoFn;
  modifyItemProduct?: ModifyItemProductFn;
  modifyItemCalculation?: ModifyItemCalculationFn;
}) => {
  const { t } = useTranslate(["Quote", "Global", "Decisions"]);

  const isTimeAndMaterialContract =
    TIME_AND_MATERIAL_CONTRACT_TYPES.includes(contractType);

  const {
    handleDecisionPreselectionChange,
    handleContingencyPreselectionChange,
    handleFinalizeContingencyDecision,
    handleFinalizeSubitemDecision,
    canForceFinalizeSubitemDecision,
    canFinalizeContingencyDecision,
    loading,
  } = useDecisionItemMutations<TemplateQuotePreviewItems_ItemFragment>(
    null,
    docId,
    false,
    expandedStore.expandedItemIds,
    noopRefetchWizardQuestions
  );

  const collapseItemAndChildrenRecursively = React.useCallback(
    (item: TemplateQuotePreviewItems_ItemFragment) => {
      const toCollapseIds = allDocItems
        .filter(i => i.pathForPdf.startsWith(item.pathForPdf))
        .map(i => i.id);
      const toExpandItemIds = expandedStore.areAllItemsExpanded
        ? allDocItems
            .filter(i => i.hasChildren && !toCollapseIds.includes(i.id))
            .map(i => i.id)
        : [];
      expandedStore.setExpandedState(
        Object.fromEntries([
          [ALL_KEY, false],
          ...toExpandItemIds.map(id => [id, true]),
          ...toCollapseIds.map(id => [id, false]),
        ])
      );
    },
    [expandedStore, allDocItems]
  );

  const fields = React.useMemo(
    (): Fields<TemplateQuotePreviewItems_ItemFragment> => ({
      CollapseButton: {
        render: item => {
          const expanded = expandedStore.isItemExpanded(item.id);
          return item.hasChildren ? (
            <CollapseButton
              aria-label={expanded ? "Collapse" : "Expand"}
              isExpanded={expanded}
              setIsExpanded={newExpanded => {
                if (newExpanded) {
                  expandedStore.setItemExpanded(item.id, newExpanded);
                } else {
                  collapseItemAndChildrenRecursively(item);
                }
              }}
            />
          ) : null;
        },
      },
      NonBindingCaption: {
        render: item => {
          if (item.type === "paid" && item.binding === "non_binding") {
            return t("Non-Binding item", {
              ns: "Quote",
            });
          }

          return null;
        },
      },
      ContingencyOptionalCaption: {
        render: item => {
          if (item.decisionIsContingentItem) {
            const captions: string[] = [];
            captions.push(
              t("Optional item", {
                ns: "Quote",
              })
            );

            if (item.decisionContingentItemPreselection) {
              captions.push(
                t("Recommended", {
                  ns: "Quote",
                })
              );
            }

            return captions.join(" • ");
          }

          return null;
        },
      },
      ContingencyOptionsButtons: {
        render: item => {
          return item.decisionIsContingentItem ? (
            <ContingencyCheckbox
              item={item}
              loading={loading}
              disabled={item.originDocId !== docId}
              canEdit={canEdit}
              handleContingencyPreselectionChange={
                handleContingencyPreselectionChange
              }
            />
          ) : null;
        },
      },
      ContingencyFinalizeButton: {
        render: item => {
          return item.decisionIsContingentItem ? (
            <Button
              disabled={true}
              size="extra-small"
              variant="contained"
              color="primary"
              onClick={async e => {
                e.preventDefault();
                e.stopPropagation();
                await handleFinalizeContingencyDecision(item.id);
              }}
            >
              {t("Resolve", { ns: "Quote" })}
            </Button>
          ) : null;
        },
      },
      DecisionOptionButton: {
        render: (item, parent) => {
          return parent?.type === "decision" &&
            !item.decisionOptionElimination ? (
            <DecisionOptionRadioOrCheckbox
              docId={docId}
              projectId={null}
              item={item}
              disabled={!canEdit || loading || parent.originDocId !== docId}
              expandedItemIds={expandedStore.expandedItemIds}
            />
          ) : null;
        },
      },
      DecisionNoneOptionButton: {
        render: item => {
          return item.decisionBehaviorOfSubitems === "SELECT_ONE" ? (
            <SubitemsOptionsNoneSection
              item={item}
              loading={loading}
              disabled={item.originDocId !== docId}
              canEdit={canEdit}
              handleDecisionPreselectionChange={
                handleDecisionPreselectionChange
              }
            />
          ) : null;
        },
      },
      DecisionButton: {
        render: item => null,
      },
      DecisionFinalizeButton: {
        render: item => {
          return item.type === "decision" ? (
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                flexWrap: "wrap",
              }}
            >
              <Button
                disabled={true}
                size="extra-small"
                variant="contained"
                color="primary"
                onClick={async e => {
                  e.preventDefault();
                  e.stopPropagation();

                  if (item.decisionIsContingentItem) {
                    if (item.decisionContingentItemPreselection !== true) {
                      await handleContingencyPreselectionChange(item.id, true);
                    }
                    await handleFinalizeContingencyDecision(item.id);
                  }

                  await handleFinalizeSubitemDecision(item.id);
                }}
              >
                {t("Finalize decision", { ns: "Quote" })}
              </Button>
            </Stack>
          ) : null;
        },
      },
      Pos: {
        render: item => <TextWithOverflow>{item.pathForPdf}</TextWithOverflow>,
      },
      Title: {
        render: item => {
          if (item.isRootItem) return null;
          if (!canEdit) {
            // No `<Typography variant="h3">` wrapper — the title inherits
            // typography from the surrounding column / row context, matching
            // the editable branch below and the quote preview pattern.
            return <Box sx={{ wordBreak: "break-word" }}>{item.title}</Box>;
          }
          return (
            <NoPropagateBox preventDefault={false}>
              <EditableTypography
                variant="inherit"
                value={item.title}
                allowEmpty={false}
                sx={{ wordBreak: "break-word", mr: 1 }}
                placeholder={t("Item title", { ns: "Quote" })}
                onChange={async value => {
                  await modifyItemInfo(item.id, { title: value });
                  await refetch?.();
                }}
              />
            </NoPropagateBox>
          );
        },
      },
      Description: {
        render: item => {
          if (!templateQuoteItemHasDescription(item)) return null;
          if (item.isRootItem) return null;
          return (
            <TemplateQuotePreviewItemDescription
              item={item}
              columns={3}
              editing={
                canEdit
                  ? {
                      onChangeDescription: async value => {
                        await modifyItemInfo(item.id, { description: value });
                        await refetch?.();
                      },
                      onChangeProductTitle: async value => {
                        await modifyItemProduct(item.id, {
                          productTitle: value,
                        });
                        await refetch?.();
                      },
                      onChangeProductDescription: async value => {
                        await modifyItemProduct(item.id, {
                          productDescription: value,
                        });
                        await refetch?.();
                      },
                    }
                  : undefined
              }
            />
          );
        },
      },
      Images: {
        render: item => {
          const images = item.attachments
            .filter(a => a.clientVisibility)
            .map(processAttachment)
            .filter(isImageOr3dModel);
          return images.length > 0 ? (
            <GalleryRow
              images={images}
              maxItems={4}
              style={item.level > 2 ? { maxWidth: "300px" } : undefined}
              // objectFit="contain"
            />
          ) : null;
        },
      },
      Attachments: {
        render: item => {
          const files = item.attachments
            .filter(a => a.clientVisibility)
            .map(processAttachment)
            .filter(isPdfOrFile);
          return files.length > 0 ? (
            <CollapseSection
              title={t("Attachments", { ns: "Quote" })}
              titleVariant="body2"
              titleColor="secondary"
              iconButtonColor="secondary"
              sx={{ minHeight: "auto" }}
              isInitiallyExpanded={false}
              collapseTimeout={0}
            >
              <List
                disablePadding
                dense
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                }}
              >
                {files.map(attachment => (
                  <FileListItemButton
                    key={attachment.id}
                    file={attachment}
                    sx={{ minWidth: "200px", maxWidth: "320px", width: "auto" }}
                  />
                ))}
              </List>
            </CollapseSection>
          ) : null;
        },
      },
      Quantity: {
        render: (item, _, priceData) => {
          if (canEdit && item.proposedCalculation) {
            return (
              <EditableQuantityCell
                projectId={null}
                docId={docId}
                itemId={item.id}
                itemType={item.type}
                decisionBehaviorOfSubitems={item.decisionBehaviorOfSubitems}
                quantity={item.proposedCalculation.quantity}
                quantityUnit={item.proposedCalculation.quantityUnit}
                timePerUnit={item.proposedCalculation.timePerUnit}
                quantityExpression={
                  item.attributeExpressions.find(
                    e => e.attribute === "estimatedQuantity"
                  )?.expr
                }
                isTimeAndMaterialContract={isTimeAndMaterialContract}
                onCommitQuantity={async value => {
                  await modifyItemCalculation(item.id, {
                    estimatedQuantity: value,
                  });
                  await refetch?.();
                }}
                onCommitUnit={async value => {
                  await modifyItemCalculation(item.id, {
                    quantityUnit: value,
                  });
                  await refetch?.();
                }}
                onCommitTime={async value => {
                  await modifyItemCalculation(item.id, {
                    estimatedTime: value,
                  });
                  await refetch?.();
                }}
                onExpressionChange={async () => {
                  await refetch?.();
                }}
              />
            );
          }
          return priceData.rows &&
            priceData.rows.length > 0 &&
            priceData.rows.some(row => row.quantity?.proposed !== undefined) ? (
            <>
              {priceData.rows?.map((row, index) => (
                <Stack key={index}>{row.quantity?.proposed}</Stack>
              ))}
            </>
          ) : null;
        },
      },
      UnitPrice: {
        render: (item, _, priceData) =>
          priceData.rows &&
          priceData.rows.length > 0 &&
          priceData.rows.some(row => row.unitPrice?.proposed !== undefined) ? (
            <>
              {priceData.rows?.map((row, index) => (
                <Stack key={index}>{row.unitPrice?.proposed}</Stack>
              ))}
            </>
          ) : null,
      },
      Subtotal: {
        render: (item, _, priceData) =>
          priceData.rows &&
          priceData.rows.length > 0 &&
          priceData.rows.some(row => row.subtotal?.proposed !== undefined) ? (
            <>
              {priceData.rows?.map((row, index) => (
                <Stack key={index}>{row.subtotal?.proposed}</Stack>
              ))}
            </>
          ) : null,
      },
      TotalPrice: {
        render: (item, _, priceData) => {
          return <>{priceData.total?.proposed}</>;
        },
      },
    }),
    [
      t,
      loading,
      docId,
      handleContingencyPreselectionChange,
      handleFinalizeContingencyDecision,
      expandedStore,
      collapseItemAndChildrenRecursively,
      handleDecisionPreselectionChange,
      canEdit,
      handleFinalizeSubitemDecision,
      modifyItemInfo,
      modifyItemProduct,
      modifyItemCalculation,
      isTimeAndMaterialContract,
      refetch,
    ]
  );

  return (
    <DocumentPreviewItems<TemplateQuotePreviewItems_ItemFragment>
      doc={{
        id: docId,
        contractType,
        isBinding,
        agreement: "NONE",
      }}
      fields={fields}
      allDocItems={allDocItems}
      navigateToItem={navigateToItem}
      selectedItemId={selectedItemId}
      isTimeAndMaterialContract={isTimeAndMaterialContract}
      getItemStyle={getTemplateQuoteItemStyle}
      shouldRenderItem={shouldRenderTemplateQuoteItem}
      container={container}
      showCollapseButton={false}
      expandedStore={undefined}
    />
  );
};

const getTemplateQuoteItemStyle = (
  theme: Theme,
  item: TemplateQuotePreviewItems_ItemFragment
): React.CSSProperties | undefined => ({
  color:
    item.decisionOptionElimination ||
    item.isParentDecisionNotPreselected ||
    item.isParentContingencyNotPreselected ||
    (item.decisionIsContingentItem &&
      item.decisionContingentItemPreselection !== true)
      ? theme.palette.grey[600]
      : item.type === "paid" && item.binding === "non_binding"
        ? theme.palette.secondary.main
        : undefined,
  fontStyle:
    item.type === "paid" && item.binding === "non_binding"
      ? "italic"
      : undefined,
  textDecoration: item.deletedAt ? "line-through" : undefined,
});

function shouldRenderTemplateQuoteItem(
  item: TemplateQuotePreviewItems_ItemFragment
) {
  return !item.isItemEliminated && !item.isAnyParentItemEliminated;
}
